<template>
    <fade-up-animation :element="() => $refs.section">
        <section
            class="vertical-text-image-section"
            :class="[
                `text-${ textAlignment }`,
                `background-${ backgroundVariant }`,
            ]"
            ref="section"
        >
            <b-container
                :fluid="fluidContainer"
                v-test:container
            >
                <mobile-gutter>
                    <div class="text">
                        <h2
                            v-if="title"
                            v-test:title
                        >{{ title }}</h2>

                        <div
                            v-if="topDesktopImage.src || topMobileImage.src"
                            class="top-image-container"
                            v-test:topImageContainer
                        >
                            <lazy-image
                                v-if="topMobileImage.src"
                                class="mobile-image top-mobile-image"
                                :src="topMobileImage.src"
                                :alt="topMobileImage.alt"
                                :width="topMobileImage.width"
                                :height="topMobileImage.height"
                                v-test:topMobileImage
                            />

                            <lazy-image
                                v-if="topDesktopImage.src"
                                class="desktop-image top-desktop-image"
                                :src="topDesktopImage.src"
                                :alt="topDesktopImage.alt"
                                :width="topDesktopImage.width"
                                :height="topDesktopImage.height"
                                v-test:topDesktopImage
                            />
                        </div>

                        <markdown-content
                            :content="text"
                        />
                    </div>
                </mobile-gutter>
            </b-container>

            <div
                v-if="bottomDesktopImage.src || bottomMobileImage.src"
                class="bottom-image-container"
                v-test:bottomImageContainer
            >
                <lazy-image
                    v-if="bottomMobileImage.src"
                    class="mobile-image bottom-mobile-image"
                    :src="bottomMobileImage.src"
                    :alt="bottomMobileImage.alt"
                    :width="bottomMobileImage.width"
                    :height="bottomMobileImage.height"
                    v-test:bottomMobileImage
                />

                <lazy-image
                    v-if="bottomDesktopImage.src"
                    class="desktop-image bottom-desktop-image"
                    :src="bottomDesktopImage.src"
                    :alt="bottomDesktopImage.alt"
                    :width="bottomDesktopImage.width"
                    :height="bottomDesktopImage.height"
                    v-test:bottomDesktopImage
                />
            </div>
        </section>
    </fade-up-animation>
</template>

<script>
    import MarkdownContent from '../../utils/MarkdownContent/MarkdownContent';
    import { ITSU_VARIANT_DEFAULT, ITSU_VARIANTS } from '../../../plugins/constants';

    export default {
        name: 'vertical-text-image-section',

        components: {
            MarkdownContent,
        },

        props: {
            title: {
                type: String,
                default: '',
            },

            text: {
                type: String,
                required: true,
            },

            topDesktopImage: {
                type: Object,
                default: () => ({}),
            },

            topMobileImage: {
                type: Object,
                default: () => ({}),
            },

            bottomDesktopImage: {
                type: Object,
                default: () => ({}),
            },

            bottomMobileImage: {
                type: Object,
                default: () => ({}),
            },

            backgroundImage: {
                type: Object,
                default: () => ({}),
            },

            fluidContainer: {
                type: Boolean,
                default: false,
            },

            textAlignment: {
                type: String,
                default: 'left',
                validator: (val) => ['left', 'centered'].includes(val),
            },

            backgroundVariant: {
                type: String,
                default: ITSU_VARIANT_DEFAULT,
                validator: (val) => ITSU_VARIANTS.includes(val),
            },
        },
    };
</script>

<style lang="scss" scoped>
    $desktop-breakpoint: md;

    .vertical-text-image-section {
        font-size: rem(20px);
        overflow: hidden;

        @include media-breakpoint-up($desktop-breakpoint) {
            font-size: rem(24px);
        }
    }

    .text-centered {
        text-align: center;
    }

    .container {
        @include media-breakpoint-only(sm) {
            max-width: none;
        }
    }

    @each $variant, $colour in $itsu-variants {
        .background-#{ $variant } {
            background-color: $colour;
        }
    }

    .background-blue,
    .background-pink,
    .background-yellow {
        h2 {
            color: inherit;
        }
    }

    .background-blue,
    .background-pink {
        color: #ffffff;
    }

    .mobile-image {
        @include media-breakpoint-up($desktop-breakpoint) {
            display: none;
        }
    }

    .desktop-image {
        display: none;

        @include media-breakpoint-up($desktop-breakpoint) {
            display: inline;
        }
    }

    img {
        max-width: 100%;
        height: auto;
    }

    .top-image-container {
        margin-bottom: 35px;

        @include media-breakpoint-up($desktop-breakpoint) {
            margin-bottom: 45px;
        }
    }

    .text {
        padding: 45px 0;

        @include media-breakpoint-up($desktop-breakpoint) {
            padding: 85px 0;
        }
    }

    h2 {
        font-weight: bold;
        color: primary-palette(1);
        margin-bottom: 0.6em;
    }

    .text ::v-deep {
        p:not(:last-child) {
            margin-bottom: 2em;

            @include media-breakpoint-up($desktop-breakpoint) {
                margin-bottom: 2.5em;
            }
        }

        p:last-child {
            margin-bottom: 0;
        }
    }

    .bottom-image-container {
        text-align: center;
    }
</style>
